import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, RefreshCw, ChevronDown, ChevronRight, User } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STEP_LABELS = {
    photo_uploaded: "Photo uploaded",
    background_removed: "Background removed",
    preview_2d_ready: "2D preview generated",
    style_skipped: "Skipped 2D styling",
    generation_started: "3D generation started",
    generation_completed: "3D model ready",
    stl_uploaded: "Uploaded own STL",
    size_quoted: "Size & price calculated",
    stl_downloaded: "STL downloaded",
    checkout_started: "Proceeded to checkout",
};

const stepLabel = (s) => STEP_LABELS[s] || s;

const fmtTime = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
};

const ownerLabel = (o) => (o?.startsWith("guest:") ? `Guest · ${o.slice(6, 20)}` : o || "unknown");
const ownerKey = (o) => (o || "unknown").replace(/[^a-zA-Z0-9]/g, "-");

const metaLine = (s) => {
    const m = s.meta || {};
    const parts = [];
    if (m.style) parts.push(`style: ${m.style}`);
    if (m.height_mm) parts.push(`${m.height_mm} mm`);
    if (m.quantity) parts.push(`qty ${m.quantity}`);
    if (m.total_inr != null) parts.push(`₹${m.total_inr}`);
    if (m.weight_g) parts.push(`${m.weight_g} g`);
    return parts.join(" · ");
};

const AdminMiniatureActivity = () => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/miniature/admin/activity");
            setSessions(data.sessions || []);
        } catch {
            toast.error("Could not load miniature activity");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-6" data-testid="admin-miniature-activity-page">
            <div className="flex justify-between items-center">
                <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Activity className="w-6 h-6 text-primary" /> Miniature Activity</h1>
                <Button onClick={load} variant="outline" className="rounded-full border-primary/40 text-primary" data-testid="activity-refresh-btn"><RefreshCw className="w-4 h-4" /> Refresh</Button>
            </div>
            <p className="text-sm text-muted-foreground">Every step customers take in the Order Your Miniature flow — newest sessions first. Guests are identified by their anonymous session.</p>

            <div className="rounded-3xl glass p-6">
                {sessions.length === 0 && !loading && <div className="text-sm text-muted-foreground" data-testid="activity-empty">No activity recorded yet.</div>}
                <div className="divide-y divide-border/50">
                    {sessions.map((sess) => (
                        <div key={sess.owner} data-testid={`activity-session-${ownerKey(sess.owner)}`}>
                            <button
                                className="w-full flex items-center gap-3 py-3 text-left hover:bg-muted/30 rounded-xl px-2 transition-colors"
                                onClick={() => setOpen(open === sess.owner ? null : sess.owner)}
                                data-testid={`activity-session-toggle-${ownerKey(sess.owner)}`}
                            >
                                {open === sess.owner ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                                <User className="w-4 h-4 text-primary" />
                                <span className="font-medium text-sm">{ownerLabel(sess.owner)}</span>
                                <span className="text-xs text-muted-foreground">{sess.step_count} steps</span>
                                <span className="ml-auto text-xs text-muted-foreground">last: {stepLabel(sess.last_step)} · {fmtTime(sess.last_at)}</span>
                            </button>
                            {open === sess.owner && (
                                <div className="ml-9 mb-3 space-y-1" data-testid={`activity-timeline-${ownerKey(sess.owner)}`}>
                                    {sess.steps.map((s) => (
                                        <div key={s.id} className="flex items-baseline gap-3 text-sm py-1" data-testid={`activity-step-${s.id}`}>
                                            <span className="text-xs text-muted-foreground w-44 shrink-0">{fmtTime(s.at)}</span>
                                            <span className="font-medium">{stepLabel(s.step)}</span>
                                            {metaLine(s) && <span className="text-xs text-muted-foreground">{metaLine(s)}</span>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AdminMiniatureActivity;
